import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Button,
  Tooltip,
  DialogContent,
  Dialog,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import LockIcon from "@mui/icons-material/Lock";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import dayjs from "dayjs";
import {
  useGetApiSmsByPhoneSearchQuery,
  useGetTodayApiSmsReportCSVMutation,
} from "../store/services/reportApi";
import {
  useGetApisendreportQuery,
  useGetRcsTemplateViewQuery,
} from "../store/services/rcstemplateApi";
import {
  useGetapireportQuery,
  useGetapisenttemplateQuery,
} from "../store/services/whatsappApi";
import {
  useGetsmsapisentreportQuery,
  useGetsmsapireportcountQuery,
} from "../store/services/smsApi";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

/* ────────────────────────── Styles ────────────────────────── */
// Exact match of the delivery report's `inputSx`
const inputSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

/* ─────────────────────────────────────────────────────────────────────────
   Helper components / functions for both preview dialogs.
   ───────────────────────────────────────────────────────────────────────── */

const statusStyle = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "APPROVED") return { bg: "#25D366", color: "#fff" };
  if (s === "REJECTED" || s === "DISABLED" || s === "PAUSED")
    return { bg: "#d32f2f", color: "#fff" };
  if (s === "PENDING" || s === "IN_APPEAL")
    return { bg: "#FFA726", color: "#fff" };
  return { bg: "rgba(255,255,255,0.25)", color: "#fff" };
};

const getOTPButtonLabel = (otpType) => {
  const t = (otpType || "").toUpperCase();
  if (t === "COPY_CODE") return "Copy code";
  if (t === "ONE_TAP") return "Autofill";
  if (t === "ZERO_TAP") return "Verify";
  return "Verify";
};

// Detect if a URL points to a video so we can render <video> instead of <img>.
const isVideoUrl = (url) => {
  if (!url) return false;
  const s = String(url).toLowerCase();
  return (
    s.endsWith(".mp4") ||
    s.endsWith(".webm") ||
    s.endsWith(".mov") ||
    s.includes("/video/") ||
    s.includes("video%2F")
  );
};

// Extracts a sensible tooltip for an RCS button (URL or postback).
const getButtonTooltip = (btn) => {
  if (!btn) return "";
  if (btn.url) return btn.url;
  if (btn.suggestion_postback) return btn.suggestion_postback;
  return "";
};

const HeaderMedia = ({ url, format = "IMAGE", height = 110 }) => {
  if (!url) {
    return (
      <Box
        sx={{
          height,
          bgcolor: "#E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.65rem", color: "#64748b" }}>
          {format}
        </Typography>
      </Box>
    );
  }

  if (format === "VIDEO") {
    return (
      <Box
        sx={{
          height,
          bgcolor: "#000",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <video
          src={url}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <PlayArrowIcon
            sx={{ fontSize: 40, color: "rgba(255,255,255,0.85)" }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <img
      src={url}
      alt={format}
      style={{ width: "100%", height, objectFit: "cover", display: "block" }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
};

const CarouselPreviewGray = ({ cards = [] }) => {
  const [idx, setIdx] = useState(0);
  if (!cards.length) return null;

  const card = cards[idx];
  const header = card.components?.find((c) => c.type === "HEADER");
  const body = card.components?.find((c) => c.type === "BODY");
  const buttons = card.components?.find((c) => c.type === "BUTTONS");
  const imgUrl = header?.example?.header_handle?.[0];

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <IconButton
          size="small"
          disabled={idx === 0}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          sx={{ p: 0.3 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography sx={{ fontSize: "0.6rem", color: "#666" }}>
          Card {idx + 1} of {cards.length}
        </Typography>
        <IconButton
          size="small"
          disabled={idx === cards.length - 1}
          onClick={() => setIdx((i) => Math.min(cards.length - 1, i + 1))}
          sx={{ p: 0.3 }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {imgUrl && (
        <img
          src={imgUrl}
          alt="card"
          style={{
            width: "100%",
            height: 100,
            objectFit: "cover",
            borderRadius: 6,
          }}
        />
      )}

      {body?.text && (
        <Typography sx={{ fontSize: "0.75rem", color: "#333", mt: 0.5 }}>
          {body.text}
        </Typography>
      )}

      {buttons?.buttons?.map((btn, i) => (
        <Button
          key={i}
          fullWidth
          size="small"
          sx={{
            fontSize: "0.65rem",
            mt: 0.5,
            borderRadius: "6px",
            textTransform: "none",
          }}
        >
          {btn.text}
        </Button>
      ))}
    </Box>
  );
};

/* ───────────────────────────────────────────────────────────────────────── */

const APIReport = () => {
  const navigate = useNavigate();
  const [startdate, setStartDate] = useState(dayjs());
  const [enddate, setEndDate] = useState(dayjs());
  const [smsStatus, setSmsStatus] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [service, setService] = useState("SMS");

  const [searchTrigger, setSearchTrigger] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [appliedPhoneNumber, setAppliedPhoneNumber] = useState("");
  const [customerid, setCustomerid] = useState("");
  const [templatename, setTemplatename] = useState("");

  // ── WhatsApp template preview dialog state ──────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // ── RCS template preview dialog state ───────────────────────────────────
  const [rcstemplateid, setRcstemplateid] = useState("");
  const [rcsPreviewOpen, setRcsPreviewOpen] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);

  // ── Decode JWT once on mount ─────────────────────────────────────────────
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Token not found");
        return;
      }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;

      if (!id) {
        toast.error("Customer not found");
      } else {
        setCustomerid(id);
      }
    } catch (error) {
      toast.error("Invalid access token format");
    }
  }, []);

  console.log("customerid", customerid);

  const limit = 25;
  const formattedStartDate = startdate
    ? startdate.format("YYYY-MM-DD")
    : undefined;
  const formattedEndDate = enddate ? enddate.format("YYYY-MM-DD") : undefined;

  const [downloadTodayReport] = useGetTodayApiSmsReportCSVMutation();

  // ── SMS sent-report list query ───────────────────────────────────────────
  const {
    data: smsSentReportData,
    isLoading: isSmsSentLoading,
    error: smsSentError,
    refetch: refetchSmsSentReport,
  } = useGetsmsapisentreportQuery(
    {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      page,
      limit,
    },
    {
      skip: !!appliedPhoneNumber || !shouldFetch || service !== "SMS",
      refetchOnMountOrArgChange: false,
    },
  );

  // ── WhatsApp API report (one row per campaign) ──────────────────────────
  const {
    currentData: whatsappapireport,
    isLoading: whatsappLoading,
    isFetching: whatsappFetching,
    error: whatsappError,
    refetch: refetchWhatsapp,
  } = useGetapireportQuery(
    {
      customer_id: customerid,
      page,
      limit,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    },
    {
      skip: service !== "WHATSAPP" || !customerid || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  // ── WhatsApp template preview query ─────────────────────────────────────
  const {
    currentData: whatsapptemplteselect,
    isLoading: isTemplateLoading,
    isFetching: isTemplateFetching,
  } = useGetapisenttemplateQuery(
    { customer_id: customerid, template_name: templatename },
    { skip: !templatename || !customerid },
  );

  // Push fetched template into `selectedTemplate`. Response shape:
  //   { customer_id, templates: [ {...} ] }
  useEffect(() => {
    if (!previewOpen) return;
    const t = whatsapptemplteselect?.templates?.[0];
    if (t) setSelectedTemplate(t);
  }, [whatsapptemplteselect, previewOpen]);

  // ── RCS template view query ─────────────────────────────────────────────
  const {
    currentData: rcstemplateview,
    isLoading: isRcsTemplateLoading,
    isFetching: isRcsTemplateFetching,
  } = useGetRcsTemplateViewQuery(rcstemplateid, { skip: !rcstemplateid });

  // ── RCS campaign list query ─────────────────────────────────────────────
  const {
    data: rcsData,
    isLoading: isRcsLoading,
    error: rcsError,
    refetch: refetchRcs,
  } = useGetApisendreportQuery(
    {
      startdate: formattedStartDate,
      enddate: formattedEndDate,
      page,
      limit,
    },
    {
      skip: service !== "RCS" || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  console.log("rcs data", rcsData);
  console.log("rcs template view", rcstemplateview);

  // ── Dedicated SMS summary query ─────────────────────────────────────────
  const {
    data: smsSummaryData,
    isLoading: smsSummaryLoading,
    isError: smsSummaryError,
  } = useGetsmsapireportcountQuery(
    { start_date: formattedStartDate, end_date: formattedEndDate },
    {
      skip: !shouldFetch || !!appliedPhoneNumber || service !== "SMS",
    },
  );

  const isValidPhone = appliedPhoneNumber && appliedPhoneNumber.length === 10;

  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useGetApiSmsByPhoneSearchQuery(appliedPhoneNumber, {
    skip: !isValidPhone || !shouldFetch,
  });

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    setShouldFetch(false);
    setSearchTrigger(false);
    if (enddate && newValue && enddate.diff(newValue, "month", true) > 1) {
      setEndDate(newValue.add(1, "month"));
    }
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    setShouldFetch(false);
    setSearchTrigger(false);
  };

  const handleServiceChange = (e) => {
    setService(e.target.value);
    setShouldFetch(false);
    setSearchTrigger(false);
    setPage(1);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) setPhoneNumber(value);
    setShouldFetch(false);
    setSearchTrigger(false);
  };

  const handleSearch = () => {
    setAppliedPhoneNumber(phoneNumber);
    setShouldFetch(true);
    setSearchTrigger(true);
    setPage(1);
  };

  const handleClearSearch = () => {
    setPhoneNumber("");
    setAppliedPhoneNumber("");
    setShouldFetch(false);
    setSearchTrigger(false);
    setPage(1);
  };

  const handleOpenTemplatePreview = (templateName) => {
    setSelectedTemplate(null);
    setTemplatename(templateName);
    setPreviewOpen(true);
  };

  const handleCloseTemplatePreview = () => {
    setPreviewOpen(false);
    setSelectedTemplate(null);
  };

  // ── RCS preview handlers ────────────────────────────────────────────────
  const handleOpenRcsPreview = (templateId) => {
    setSelectedCardIndex(0);
    setRcstemplateid(templateId);
    setRcsPreviewOpen(true);
  };

  const handleCloseRcsPreview = () => {
    setRcsPreviewOpen(false);
    setRcstemplateid("");
    setSelectedCardIndex(0);
  };

  // ── Refetch depending on service / phone search ─────────────────────────
  useEffect(() => {
    if (searchTrigger && shouldFetch) {
      if (service === "RCS") {
        refetchRcs();
      } else if (service === "WHATSAPP") {
        refetchWhatsapp();
      } else if (appliedPhoneNumber && appliedPhoneNumber.length === 10) {
        refetchSearch();
      } else {
        refetchSmsSentReport();
      }
      setSearchTrigger(false);
    }
  }, [
    searchTrigger,
    shouldFetch,
    appliedPhoneNumber,
    service,
    refetchSearch,
    refetchSmsSentReport,
    refetchRcs,
    refetchWhatsapp,
  ]);

  useEffect(() => {
    setPage(1);
  }, [startdate, enddate, smsStatus]);

  const handlePageChange = (e, value) => {
    setPage(value);
    if (shouldFetch) {
      setSearchTrigger(true);
    }
  };

  const getTableHeaders = () => {
    switch (service) {
      case "RCS":
        return [
          "Sr No",
          "Campaign Name",
          "Bot Name",
          "Template Name",
          "Created At",
          "Executed At",
          "Campaign Category",
          "Status",
          "RCS Overview",
          "Actions",
        ];
      case "WHATSAPP":
        return [
          "Campaign ID",
          "Campaign Name",
          "Source",
          "Template",
          "Template Category",
          "Total Numbers",
          "Status",
          "Actions",
        ];
      default:
        return [
          "Sr No",
          "Apireceive ID",
          "Template Name",
          "SenderID",
          "Total Numbers",
          "Created At",
          "Status",
          "SMS Overview",
          "Action",
        ];
    }
  };

  const rows = isValidPhone
    ? searchData?.data || []
    : smsSentReportData?.data || [];
  const isLoading = isValidPhone ? isSearchLoading : isSmsSentLoading;
  const error = isValidPhone ? searchError : smsSentError;

  const rcsRows = rcsData?.data || [];

  const whatsappRows = whatsappapireport?.data || [];
  const whatsappSummary = whatsappapireport?.summary || {};

  /* ────────────── RCS totals across fetched campaigns ────────────── */
  const rcsTotals = rcsRows.reduce(
    (acc, row) => {
      const ov = row.rcs_overview || {};
      acc.total += ov.total || 0;
      acc.submit += ov.submit || 0;
      acc.sent += ov.sent || 0;
      acc.delivered += ov.delivered || 0;
      acc.pending += ov.pending || 0;
      acc.read += ov.read || 0;
      acc.click += ov.click || 0;
      acc.failed += ov.failed || 0;
      acc.rejected += ov.rejected || 0;
      acc.disabled += ov.disabled || 0;
      acc.limitExhausted += ov.limitExhausted || 0;
      return acc;
    },
    {
      total: 0,
      submit: 0,
      sent: 0,
      delivered: 0,
      pending: 0,
      read: 0,
      click: 0,
      failed: 0,
      rejected: 0,
      disabled: 0,
      limitExhausted: 0,
    },
  );

  /* ────────────── RCS Overview cell renderer ────────────── */
  const renderRcsOverview = (ov = {}) => {
    const line = (label, value) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "#94a3b8",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{ fontSize: "0.72rem", color: "#475569", lineHeight: 1.3 }}
        >
          {label}:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>
            {value ?? 0}
          </Box>
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {line("Sent", ov.sent)}
        {line("Delivered", ov.delivered)}
        {line("Read", ov.read)}
        {line("Clicks", ov.click)}
        {line("Failed", ov.failed)}
      </Box>
    );
  };

  /* ────────────── SMS Overview cell renderer ────────────── */
  const renderSmsOverview = (ov = {}) => {
    const line = (label, value) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "#94a3b8",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{ fontSize: "0.72rem", color: "#475569", lineHeight: 1.3 }}
        >
          {label}:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "#0f172a" }}>
            {value ?? 0}
          </Box>
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {line("Sent", ov.sent)}
        {line("Delivered", ov.delivrd)}
        {line("Pending", ov.pending)}
        {line("Failed", ov.failed)}
        {line("Rejected", ov.rejected)}
      </Box>
    );
  };

  /* ────────────── WhatsApp Overview cell renderer ────────────── */
  const renderWhatsappOverview = (bs = {}) => {
    const line = (label, value, danger = false) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: danger ? "#dc2626" : "#94a3b8",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{ fontSize: "0.72rem", color: "#475569", lineHeight: 1.3 }}
        >
          {label}:{" "}
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              color: danger ? "#dc2626" : "#0f172a",
            }}
          >
            {value ?? 0}
          </Box>
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {line("Sent", bs.sent)}
        {line("Delivered", bs.delivered)}
        {line("Read", bs.read)}
        {line("Failed", bs.failed, (bs.failed ?? 0) > 0)}
        {line("Replied", bs.replied)}
      </Box>
    );
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        API Delivery Report
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startdate}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={enddate}
            onChange={handleEndDateChange}
            maxDate={startdate ? startdate.add(1, "month") : undefined}
            minDate={startdate}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel
            id="service-select-label"
            sx={{ fontSize: "0.75rem" }}
          >
            Service
          </InputLabel>
          <Select
            labelId="service-select-label"
            value={service}
            onChange={handleServiceChange}
            label="Service"
            sx={{
              height: 35,
              fontSize: "0.75rem",
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.8)",
              transition: "all 0.2s ease",
              "& .MuiSelect-select": {
                py: 0.5,
                fontSize: "0.75rem",
              },
              "&:hover": {
                boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
              },
              "&.Mui-focused": {
                boxShadow: "0 0 0 2px rgba(14,165,233,0.2)",
              },
            }}
          >
            <MenuItem value="SMS" sx={{ fontSize: "0.75rem" }}>
              SMS
            </MenuItem>
            <MenuItem value="RCS" sx={{ fontSize: "0.75rem" }}>
              RCS
            </MenuItem>
            <MenuItem value="WHATSAPP" sx={{ fontSize: "0.75rem" }}>
              WHATSAPP
            </MenuItem>
          </Select>
        </FormControl>

        {service === "SMS" && (
          <TextField
            label="Search Phone Number"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            size="small"
            sx={{
              ...inputSx,
              width: 150,
            }}
          />
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          sx={{
            fontSize: "0.75rem",
            textTransform: "none",
            bgcolor: "#0288d1",
            "&:hover": { bgcolor: "#0277bd" },
            minWidth: 80,
          }}
        >
          Search
        </Button>

        {service === "SMS" && appliedPhoneNumber && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearSearch}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              minWidth: 80,
            }}
          >
            Clear
          </Button>
        )}
      </Paper>

      {/* ──────── SMS Status Summary ──────── */}
      {!appliedPhoneNumber && service === "SMS" && shouldFetch && (
        <>
          {smsSummaryLoading ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              Loading summary…
            </Typography>
          ) : smsSummaryError || !smsSummaryData?.status ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: "0.75rem", mt: 0.5 }}
            >
              Failed to load summary
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              {smsSummaryData.data}
            </Typography>
          )}
        </>
      )}

      {/* ──────── RCS Summary ──────── */}
      {service === "RCS" && shouldFetch && (
        <>
          {isRcsLoading ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              Loading summary…
            </Typography>
          ) : rcsError ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: "0.75rem", mt: 0.5 }}
            >
              Failed to load summary
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", mt: 0.5, color: "#1b1c1d" }}
            >
              Total: <b>{rcsTotals.total}</b> | Submitted:{" "}
              <b>{rcsTotals.submit}</b> | Sent: <b>{rcsTotals.sent}</b> |
              Delivered: <b>{rcsTotals.delivered}</b>| Pending:
              <b>{rcsTotals.pending}</b> | Read: <b>{rcsTotals.read}</b> |
              Clicks: <b>{rcsTotals.click}</b> | Failed:{" "}
              <b>{rcsTotals.failed}</b> | Rejected: <b>{rcsTotals.rejected}</b>{" "}
              |Disabled:<b>{rcsTotals.disabled}</b>| Limit exhausted:
              <b>{rcsTotals.limitExhausted}</b>
            </Typography>
          )}
        </>
      )}

      {/* ──────── WhatsApp Summary ──────── */}
      {service === "WHATSAPP" && shouldFetch && (
        <>
          {whatsappLoading ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              Loading summary…
            </Typography>
          ) : whatsappError ? (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: "0.75rem", mt: 0.5 }}
            >
              Failed to load summary
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", mt: 0.5, color: "#1b1c1d" }}
            >
              Batches: <b>{whatsappSummary?.total_batches ?? 0}</b> | Messages:{" "}
              <b>{whatsappSummary?.total_messages ?? 0}</b> | Success:{" "}
              <b>{whatsappSummary?.successful_messages ?? 0}</b> | Failed:{" "}
              <b>{whatsappSummary?.failed_messages ?? 0}</b> | Delivered:{" "}
              <b>{whatsappSummary?.by_status?.delivered ?? 0}</b> | Read:{" "}
              <b>{whatsappSummary?.by_status?.read ?? 0}</b> | Replied:{" "}
              <b>{whatsappSummary?.by_status?.replied ?? 0}</b> | Clicked:{" "}
              <b>{whatsappSummary?.by_status?.clicked ?? 0}</b>
            </Typography>
          )}
        </>
      )}

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                color: "white",
                fontSize: "0.8rem",
                padding: "6px",
                borderBottom: "0.5px solid #1b1b1bff",
                borderRight: "0.5px solid #1b1b1bff",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableRow>
              {getTableHeaders().map((header, index) => (
                <TableCell key={index}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* ───────────── RCS TABLE ───────────── */}
            {service === "RCS" ? (
              <>
                {isRcsLoading && shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}

                {!isRcsLoading &&
                  !rcsError &&
                  rcsRows.length > 0 &&
                  rcsRows.map((row) => (
                    <TableRow
                      key={row.apirequest_id || row.sr_no}
                      hover
                      sx={{
                        "& td": {
                          borderBottom: "0.5px solid #e2e8f0",
                        },
                      }}
                    >
                      <TableCell sx={{ ...cellSX, width: 70, maxWidth: 70 }}>
                        {row.sr_no}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 150 }}>
                        {row.campaign_name || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 120 }}>
                        {row.bot_name || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 120 }}>
                        {row.template_name || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 115, maxWidth: 115 }}>
                        {row.created_at ? (
                          <div>
                            <div>
                              {dayjs(row.created_at).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              {dayjs(row.created_at).format("HH:mm:ss")}
                            </div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 115, maxWidth: 115 }}>
                        {row.completed_at ? (
                          <div>
                            <div>
                              {dayjs(row.completed_at).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              {dayjs(row.completed_at).format("HH:mm:ss")}
                            </div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 130 }}>
                        {row.campaign_type || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 100, maxWidth: 100 }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color:
                              row.status === "EXECUTED"
                                ? "#16a34a"
                                : row.status === "FAILED"
                                  ? "#dc2626"
                                  : "#64748b",
                          }}
                        >
                          {row.status || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 140 }}>
                        {renderRcsOverview(row.rcs_overview)}
                      </TableCell>

                      <TableCell
                        sx={{ ...cellSX, width: 110, maxWidth: 110 }}
                        align="center"
                      >
                        <Tooltip title="View messages">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate("/api-sent-report", {
                                state: { rcsRequestId: row.apirequest_id },
                              })
                            }
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview template">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenRcsPreview(row.template_id)
                            }
                            disabled={!row.template_id}
                          >
                            <SmartphoneIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                {!isRcsLoading &&
                  !rcsError &&
                  rcsRows.length === 0 &&
                  shouldFetch && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No RCS campaigns found
                      </TableCell>
                    </TableRow>
                  )}

                {!shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Click Search to load data
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : service === "WHATSAPP" ? (
              /* ───────────── WHATSAPP TABLE ───────────── */
              <>
                {whatsappLoading && shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}

                {!whatsappLoading &&
                  !whatsappError &&
                  whatsappRows.length > 0 &&
                  whatsappRows.map((camp) => {
                    const bs = camp.by_status || {};
                    const firstMsg = camp.messages?.[0];
                    const templateName =
                      firstMsg?.message_data?.template?.name || "-";
                    const bookedCategory =
                      firstMsg?.message_data?.booked_category ||
                      firstMsg?.booked_category ||
                      "-";

                    return (
                      <TableRow
                        key={camp.campaign_id}
                        hover
                        sx={{
                          "& td": {
                            borderBottom: "0.5px solid #e2e8f0",
                          },
                        }}
                      >
                        <TableCell sx={{ ...cellSX, minWidth: 100 }}>
                          {camp.campaign_id || "-"}
                        </TableCell>

                        <TableCell sx={{ ...cellSX, minWidth: 180 }}>
                          <Tooltip title={camp.campaign_name || ""}>
                            <Box
                              sx={{
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {camp.campaign_name || "-"}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ ...cellSX, minWidth: 100 }}>
                          {camp.source || "-"}
                        </TableCell>

                        <TableCell sx={{ ...cellSX, minWidth: 120 }}>
                          {templateName}
                        </TableCell>

                        <TableCell sx={{ ...cellSX, minWidth: 110 }}>
                          {bookedCategory}
                        </TableCell>

                        <TableCell sx={{ ...cellSX, width: 90, maxWidth: 90 }}>
                          {camp.total_no_of_phone_numbers ?? 0}
                        </TableCell>

                        <TableCell sx={{ ...cellSX, minWidth: 140 }}>
                          {renderWhatsappOverview(bs)}
                        </TableCell>

                        <TableCell
                          sx={{ ...cellSX, width: 110, maxWidth: 110 }}
                          align="center"
                        >
                          <Tooltip title="View messages">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate("/api-sent-report", {
                                  state: {
                                    customerId: camp.customer_id,
                                    campaignId: camp.campaign_id,
                                  },
                                })
                              }
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Preview template">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenTemplatePreview(templateName)
                              }
                            >
                              <SmartphoneIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {!whatsappLoading &&
                  !whatsappError &&
                  whatsappRows.length === 0 &&
                  shouldFetch && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No WhatsApp campaigns found
                      </TableCell>
                    </TableRow>
                  )}

                {!shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Click Search to load data
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              /* ───────────── SMS TABLE ───────────── */
              <>
                {isLoading && shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !error &&
                  rows.length > 0 &&
                  rows.map((row) => (
                    <TableRow
                      key={
                        row.apireceive_id || row.gateway_request_id || row.sr_no
                      }
                      hover
                      sx={{
                        "& td": {
                          borderBottom: "0.5px solid #e2e8f0",
                        },
                      }}
                    >
                      <TableCell sx={{ ...cellSX, width: 70, maxWidth: 70 }}>
                        {row.sr_no}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 100, maxWidth: 100 }}>
                        {row.apireceive_id || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 120 }}>
                        {row.template_name || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 100, maxWidth: 100 }}>
                        {row.sender_id || "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 90, maxWidth: 90 }}>
                        {row.total_numbers ?? "-"}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 115, maxWidth: 115 }}>
                        {row.created_at ? (
                          <div>
                            <div>
                              {dayjs(row.created_at).format("DD/MM/YYYY")}
                            </div>
                            <div>
                              {dayjs(row.created_at).format("HH:mm:ss")}
                            </div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell sx={{ ...cellSX, width: 100, maxWidth: 100 }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color:
                              row.processed_status === "processed"
                                ? "#16a34a"
                                : row.processed_status === "failed"
                                  ? "#dc2626"
                                  : "#64748b",
                          }}
                        >
                          {row.processed_status || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...cellSX, minWidth: 140 }}>
                        {renderSmsOverview(row.sms_overview)}
                      </TableCell>

                      <TableCell
                        sx={{ ...cellSX, width: 60, maxWidth: 60 }}
                        align="center"
                      >
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate("/api-sent-report", {
                                state: {
                                  gatewayRequestId: row.gateway_request_id,
                                },
                              })
                            }
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                {!isLoading && !error && rows.length === 0 && shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}

                {!shouldFetch && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Click Search to load data
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legacy dialog (kept for compatibility) */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem" }}>{selectedText}</Typography>
        </DialogContent>
      </Dialog>

      {/* ══════════ RCS Template Preview Dialog ══════════ */}
      <Dialog
        open={rcsPreviewOpen}
        onClose={handleCloseRcsPreview}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: 6,
            backgroundColor: "transparent",
          }}
        >
          {/* phone card */}
          <Box
            sx={{
              width: "300px",
              minHeight: "400px",
              maxHeight: "75vh",
              backgroundColor: "#fff",
              border: "8px solid #000",
              borderRadius: "32px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            {/* status bar */}
            <Box
              sx={{
                width: "100%",
                height: "30px",
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem" }}>
                {dayjs().format("HH:mm")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <WifiIcon sx={{ fontSize: "16px" }} />
                <NetworkCellIcon sx={{ fontSize: "16px" }} />
                <BatteryFullIcon sx={{ fontSize: "16px" }} />
              </Stack>
            </Box>

            {/* close button */}
            <Button
              onClick={handleCloseRcsPreview}
              sx={{
                position: "absolute",
                top: 20,
                right: 10,
                minWidth: 0,
                padding: 0.5,
                zIndex: 10,
                color: "#000",
              }}
            >
              <ClearIcon fontSize="small" />
            </Button>

            {/* main content area */}
            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flexGrow: 1,
                backgroundColor: "#f5f5f5",
                minHeight: "300px",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#ccc",
                  borderRadius: "2px",
                  "&:hover": { bgcolor: "#999" },
                },
              }}
            >
              {isRcsTemplateLoading || isRcsTemplateFetching ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                    Loading template…
                  </Typography>
                </Box>
              ) : rcstemplateview?.data ? (
                <Stack spacing={2}>
                  {/* === Template Header === */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: "0.95rem",
                        color: "#000",
                      }}
                    >
                      {rcstemplateview.data.templateDetails?.template_name}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#f0f0f0",
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {rcstemplateview.data.templateDetails?.rcs_template_type}
                    </Box>
                  </Box>

                  {/* === Content Card === */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    {(() => {
                      const type =
                        rcstemplateview.data.templateDetails?.rcs_template_type;

                      switch (type) {
                        case "Text":
                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#333",
                                lineHeight: 1.6,
                                fontSize: "0.9rem",
                              }}
                            >
                              {rcstemplateview.data.templateDetails?.rcs_text}
                            </Typography>
                          );

                        case "Rich Card":
                          return (
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  backgroundColor: "#f0f0f0",
                                }}
                              >
                                {isVideoUrl(
                                  rcstemplateview.data.templateDetails
                                    ?.rich_card_image,
                                ) ? (
                                  <video
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: "8px",
                                      display: "block",
                                    }}
                                  >
                                    <source
                                      src={
                                        rcstemplateview.data.templateDetails
                                          ?.rich_card_image
                                      }
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                  </video>
                                ) : (
                                  <img
                                    src={
                                      rcstemplateview.data.templateDetails
                                        ?.rich_card_image
                                    }
                                    alt="Rich Card"
                                    style={{
                                      width: "100%",
                                      borderRadius: "8px",
                                      display: "block",
                                    }}
                                  />
                                )}
                              </Box>

                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#000",
                                  }}
                                >
                                  {
                                    rcstemplateview.data.templateDetails
                                      ?.rich_card_title
                                  }
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#666", lineHeight: 1.5 }}
                                >
                                  {
                                    rcstemplateview.data.templateDetails
                                      ?.rich_card_description
                                  }
                                </Typography>
                              </Box>
                            </Stack>
                          );

                        case "Carousel": {
                          const cards =
                            rcstemplateview.data.carousel_cards || [];
                          const selectedCard = cards[selectedCardIndex] || null;

                          return (
                            <Stack spacing={1.5}>
                              {/* Selector Buttons */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                }}
                              >
                                {cards.map((_, idx) => (
                                  <Button
                                    key={idx}
                                    onClick={() => setSelectedCardIndex(idx)}
                                    sx={{
                                      minWidth: "32px",
                                      height: "32px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      transition: "all 0.3s ease",
                                      ...(selectedCardIndex === idx
                                        ? {
                                            backgroundColor: "#000",
                                            color: "#fff",
                                          }
                                        : {
                                            backgroundColor: "#e0e0e0",
                                            color: "#333",
                                            "&:hover": {
                                              backgroundColor: "#ccc",
                                            },
                                          }),
                                    }}
                                  >
                                    {idx + 1}
                                  </Button>
                                ))}
                              </Box>

                              {/* Selected Card */}
                              {selectedCard ? (
                                <Stack spacing={1}>
                                  <Box
                                    sx={{
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {isVideoUrl(selectedCard.carousel_image) ? (
                                      <video
                                        controls
                                        style={{
                                          width: "100%",
                                          borderRadius: "8px",
                                          display: "block",
                                        }}
                                      >
                                        <source
                                          src={selectedCard.carousel_image}
                                          type="video/mp4"
                                        />
                                        Your browser does not support the video
                                        tag.
                                      </video>
                                    ) : (
                                      <img
                                        src={selectedCard.carousel_image}
                                        alt={`Card ${selectedCard.card_no}`}
                                        style={{
                                          width: "100%",
                                          borderRadius: "8px",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                        color: "#000",
                                      }}
                                    >
                                      {selectedCard.carousel_title}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", lineHeight: 1.5 }}
                                    >
                                      {selectedCard.carousel_description}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography color="text.secondary">
                                  No card data available.
                                </Typography>
                              )}
                            </Stack>
                          );
                        }

                        default:
                          return (
                            <Typography color="text.secondary">
                              Unknown type
                            </Typography>
                          );
                      }
                    })()}
                  </Box>

                  {/* === Buttons Card === */}
                  {rcstemplateview.data.buttonInformation?.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        p: 2,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Suggested Replies (button_action_id === 1) */}
                        {rcstemplateview.data.buttonInformation.filter(
                          (btn) => Number(btn.button_action_id) === 1,
                        ).length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "#666",
                                textTransform: "uppercase",
                                fontSize: "0.65rem",
                              }}
                            >
                              Suggested Replies
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                            >
                              {rcstemplateview.data.buttonInformation
                                .filter(
                                  (btn) => Number(btn.button_action_id) === 1,
                                )
                                .map((btn, idx) => {
                                  const tooltipTitle = getButtonTooltip(btn);
                                  return (
                                    <Tooltip
                                      key={`action1-${idx}`}
                                      title={tooltipTitle}
                                      disableHoverListener={!tooltipTitle}
                                    >
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          borderRadius: "6px",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          border: "1px solid #ccc",
                                          color: "#333",
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            backgroundColor: "#f0f0f0",
                                            borderColor: "#999",
                                          },
                                        }}
                                      >
                                        {btn.suggestion_text}
                                      </Button>
                                    </Tooltip>
                                  );
                                })}
                            </Stack>
                          </Box>
                        )}

                        {/* Suggested Actions (button_action_id !== 1) */}
                        {rcstemplateview.data.buttonInformation.filter(
                          (btn) => Number(btn.button_action_id) !== 1,
                        ).length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "#666",
                                textTransform: "uppercase",
                                fontSize: "0.65rem",
                              }}
                            >
                              Suggested Actions
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                            >
                              {rcstemplateview.data.buttonInformation
                                .filter(
                                  (btn) => Number(btn.button_action_id) !== 1,
                                )
                                .map((btn, idx) => {
                                  const tooltipTitle = getButtonTooltip(btn);
                                  return (
                                    <Tooltip
                                      key={`other-${idx}`}
                                      title={tooltipTitle}
                                      disableHoverListener={!tooltipTitle}
                                    >
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          borderRadius: "6px",
                                          textTransform: "none",
                                          fontWeight: 600,
                                          fontSize: "0.75rem",
                                          backgroundColor: "#000",
                                          color: "#fff",
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            backgroundColor: "#333",
                                          },
                                        }}
                                      >
                                        {btn.suggestion_text}
                                      </Button>
                                    </Tooltip>
                                  );
                                })}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                    No template selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ────────── WhatsApp Template Preview Dialog ────────── */}
      <Dialog
        open={previewOpen}
        onClose={handleCloseTemplatePreview}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "transparent",
            boxShadow: "none",
            p: 0,
            overflow: "visible",
          },
          "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.65)" },
        }}
      >
        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: 5,
            bgcolor: "transparent",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              width: 300,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#000",
              borderRadius: "36px",
              boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
              overflow: "hidden",
              border: "6px solid #222",
            }}
          >
            <Box
              sx={{
                height: 28,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#075E54",
              }}
            >
              <Typography
                sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#fff" }}
              >
                {dayjs().format("HH:mm")}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ color: "#fff" }}
              >
                <WifiIcon sx={{ fontSize: 13 }} />
                <NetworkCellIcon sx={{ fontSize: 13 }} />
                <BatteryFullIcon sx={{ fontSize: 13 }} />
              </Stack>
            </Box>

            <Box
              sx={{
                bgcolor: "#075E54",
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#128C7E",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}
                >
                  {selectedTemplate?.name?.[0]?.toUpperCase() ?? "W"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    color: "#fff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedTemplate?.name ?? "Template"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.59rem", color: "rgba(255,255,255,0.65)" }}
                >
                  {selectedTemplate?.category} • {selectedTemplate?.language}
                </Typography>
              </Box>
              {selectedTemplate?.status &&
                (() => {
                  const { bg, color } = statusStyle(selectedTemplate.status);
                  return (
                    <Box
                      sx={{
                        px: 0.9,
                        py: 0.2,
                        borderRadius: "8px",
                        bgcolor: bg,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.57rem", fontWeight: 700, color }}
                      >
                        {selectedTemplate.status}
                      </Typography>
                    </Box>
                  );
                })()}
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                px: 1,
                pt: 1.5,
                pb: 2,
                minHeight: 360,
                maxHeight: "62vh",
                bgcolor: "#f5f5f5",
                "&::-webkit-scrollbar": { width: "3px" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#bbb",
                  borderRadius: "2px",
                },
              }}
            >
              {isTemplateLoading || isTemplateFetching ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                    Loading template…
                  </Typography>
                </Box>
              ) : selectedTemplate ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: "#555",
                        bgcolor: "rgba(255,255,255,0.75)",
                        px: 1.5,
                        py: 0.3,
                        borderRadius: "8px",
                        display: "inline-block",
                      }}
                    >
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format(
                            "MMM D, YYYY",
                          )
                        : "Today"}
                    </Typography>
                  </Box>

                  <Box sx={{ width: "100%" }}>
                    {(() => {
                      const components = selectedTemplate?.components || [];
                      const carouselComp = components.find(
                        (c) => c.type === "CAROUSEL",
                      );
                      const buttonComp = components.find(
                        (c) => c.type === "BUTTONS",
                      );
                      const hasOTP = buttonComp?.buttons?.some(
                        (b) => b.type === "OTP",
                      );

                      if (carouselComp) {
                        const bodies = components.filter(
                          (c) => c.type === "BODY",
                        );
                        return (
                          <>
                            <CarouselPreviewGray
                              cards={carouselComp.cards || []}
                            />
                            {bodies.map((bodyComp, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: "8px",
                                  p: 1.5,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#333" }}
                                >
                                  {bodyComp.text}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        );
                      }

                      if (hasOTP) {
                        const bodyComp = components.find(
                          (c) => c.type === "BODY",
                        );
                        const footerComp = components.find(
                          (c) => c.type === "FOOTER",
                        );
                        const headerComp = components.find(
                          (c) => c.type === "HEADER",
                        );

                        return (
                          <>
                            <Box
                              sx={{
                                bgcolor: "#fff",
                                borderRadius: "8px",
                                p: 1.5,
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                borderLeft: "4px solid #d32f2f",
                              }}
                            >
                              <LockIcon
                                sx={{
                                  fontSize: 16,
                                  color: "#d32f2f",
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: "#d32f2f",
                                  }}
                                >
                                  Security verification
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: "#666",
                                    mt: 0.3,
                                  }}
                                >
                                  This is an authentication template
                                </Typography>
                              </Box>
                            </Box>

                            {headerComp?.text && (
                              <Box
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: "8px",
                                  p: 1.5,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "#000",
                                  }}
                                >
                                  {headerComp.text}
                                </Typography>
                              </Box>
                            )}

                            <Box
                              sx={{
                                bgcolor: "#fff",
                                borderRadius: "8px",
                                p: 1.5,
                                mb: 1,
                              }}
                            >
                              {bodyComp?.text ? (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#333" }}
                                >
                                  {bodyComp.text}
                                </Typography>
                              ) : bodyComp?.add_security_recommendation ? (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#333" }}
                                >
                                  Your verification code is:{" "}
                                  <strong>@x!yz</strong>
                                  <br />
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "#666",
                                      mt: 0.5,
                                    }}
                                  >
                                    Don't share this code with anyone. We'll
                                    never ask you for it.
                                  </Typography>
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#333" }}
                                >
                                  Your verification code is:{" "}
                                  <strong>@x!yz</strong>
                                </Typography>
                              )}
                            </Box>

                            {footerComp?.code_expiration_minutes && (
                              <Box
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: "8px",
                                  p: 1.5,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: "0.7rem", color: "#999" }}
                                >
                                  Code expires in{" "}
                                  {footerComp.code_expiration_minutes} minute
                                  {footerComp.code_expiration_minutes > 1
                                    ? "s"
                                    : ""}
                                </Typography>
                              </Box>
                            )}

                            {buttonComp?.buttons?.length > 0 && (
                              <Box
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: "8px",
                                  p: 1.5,
                                  mb: 1,
                                }}
                              >
                                <Stack spacing={1}>
                                  {buttonComp.buttons.map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={
                                        btn.type === "PHONE_NUMBER" &&
                                        btn.phone_number
                                          ? btn.phone_number
                                          : ""
                                      }
                                      arrow
                                      disableHoverListener={
                                        btn.type !== "PHONE_NUMBER" ||
                                        !btn.phone_number
                                      }
                                    >
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                          fontSize: "0.7rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          bgcolor: "#075E54",
                                          color: "#fff",
                                          py: 1,
                                          "&:hover": { bgcolor: "#128C7E" },
                                        }}
                                      >
                                        <LockIcon
                                          sx={{ fontSize: 14, mr: 0.5 }}
                                        />
                                        {getOTPButtonLabel(btn.otp_type)}
                                      </Button>
                                    </Tooltip>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </>
                        );
                      }

                      return (
                        <>
                          {components.some((c) => c.type === "HEADER") && (
                            <Box
                              sx={{
                                bgcolor: "#fff",
                                borderRadius: "8px",
                                overflow: "hidden",
                                mb: 1,
                              }}
                            >
                              {components
                                .filter((c) => c.type === "HEADER")
                                .map((headerComp, idx) => {
                                  const mediaUrl =
                                    headerComp?.example?.header_handle?.[0] ??
                                    null;
                                  const mediaFormat =
                                    headerComp.format ?? "IMAGE";

                                  return (
                                    <Box key={idx}>
                                      {mediaFormat === "IMAGE" && (
                                        <HeaderMedia
                                          url={mediaUrl}
                                          format="IMAGE"
                                          height={110}
                                        />
                                      )}

                                      {mediaFormat === "VIDEO" && (
                                        <HeaderMedia
                                          url={mediaUrl}
                                          format="VIDEO"
                                          height={140}
                                        />
                                      )}

                                      {mediaFormat === "DOCUMENT" && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            p: 1.5,
                                            bgcolor: "#fafafa",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 36,
                                              height: 36,
                                              borderRadius: "6px",
                                              bgcolor: "#e53935",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: "0.55rem",
                                                fontWeight: 700,
                                                color: "#fff",
                                              }}
                                            >
                                              PDF
                                            </Typography>
                                          </Box>
                                          <Typography
                                            sx={{
                                              fontSize: "0.72rem",
                                              color: "#333",
                                            }}
                                          >
                                            {mediaUrl
                                              ? "Document attached"
                                              : "No document"}
                                          </Typography>
                                        </Box>
                                      )}

                                      {headerComp.text &&
                                        mediaFormat !== "IMAGE" &&
                                        mediaFormat !== "VIDEO" &&
                                        mediaFormat !== "DOCUMENT" && (
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.8rem",
                                              color: "#000",
                                              p: 1,
                                            }}
                                          >
                                            {headerComp.text}
                                          </Typography>
                                        )}
                                      {mediaFormat === "TEXT" &&
                                        headerComp.text && (
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.8rem",
                                              color: "#000",
                                              p: 1,
                                            }}
                                          >
                                            {headerComp.text}
                                          </Typography>
                                        )}
                                    </Box>
                                  );
                                })}
                            </Box>
                          )}

                          <Box
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: "8px",
                              p: 1.5,
                              mb: 1,
                            }}
                          >
                            {components
                              .filter((c) => c.type === "BODY")
                              .map((bodyComp, idx) => (
                                <Typography
                                  key={idx}
                                  sx={{ fontSize: "0.8rem", color: "#333" }}
                                >
                                  {bodyComp.text}
                                </Typography>
                              ))}
                            {components
                              .filter((c) => c.type === "FOOTER")
                              .map((footerComp, idx) => (
                                <Typography
                                  key={idx}
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "#999",
                                    mt: 0.5,
                                  }}
                                >
                                  {footerComp.text}
                                </Typography>
                              ))}
                          </Box>

                          {components.some((c) => c.type === "BUTTONS") && (
                            <Box
                              sx={{
                                bgcolor: "#fff",
                                borderRadius: "8px",
                                p: 1.5,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: "#666",
                                  textTransform: "uppercase",
                                  fontSize: "0.65rem",
                                  mb: 0.5,
                                  display: "block",
                                }}
                              >
                                Suggested Actions
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                  mt: 0.5,
                                }}
                              >
                                {components
                                  .filter((c) => c.type === "BUTTONS")
                                  .flatMap((b) => b.buttons)
                                  .map((btn, bIdx) => (
                                    <Tooltip
                                      key={bIdx}
                                      title={
                                        btn.type === "PHONE_NUMBER" &&
                                        btn.phone_number
                                          ? btn.phone_number
                                          : ""
                                      }
                                      arrow
                                      disableHoverListener={
                                        btn.type !== "PHONE_NUMBER" ||
                                        !btn.phone_number
                                      }
                                    >
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          fontSize: "0.65rem",
                                          textTransform: "none",
                                          borderRadius: "6px",
                                          ...(bIdx === 0
                                            ? { bgcolor: "#000", color: "#fff" }
                                            : {
                                                bgcolor: "#f0f0f0",
                                                color: "#333",
                                              }),
                                        }}
                                      >
                                        {btn.text}
                                      </Button>
                                    </Tooltip>
                                  ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    <Typography
                      sx={{
                        fontSize: "0.57rem",
                        color: "#888",
                        textAlign: "right",
                        mt: 0.4,
                      }}
                    >
                      {dayjs(selectedTemplate.created_at).isValid()
                        ? dayjs(selectedTemplate.created_at).format("HH:mm")
                        : dayjs().format("HH:mm")}{" "}
                      ✓✓
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                    No template selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseTemplatePreview}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {service === "RCS"
        ? rcsData?.pagination?.totalPages > 1 &&
          shouldFetch && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                sx={{ fontSize: "0.7rem" }}
                count={rcsData?.pagination?.totalPages || 1}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )
        : service === "WHATSAPP"
          ? whatsappapireport?.pagination?.total_pages > 1 &&
            shouldFetch && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  sx={{ fontSize: "0.7rem" }}
                  count={whatsappapireport?.pagination?.total_pages || 1}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )
          : !appliedPhoneNumber &&
            smsSentReportData?.pagination?.totalPages > 1 &&
            shouldFetch && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  sx={{ fontSize: "0.7rem" }}
                  count={smsSentReportData?.pagination?.totalPages || 1}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
    </Box>
  );
};

export default APIReport;
